import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BANNER_TYPE,
  type InternalChromeConfig,
  type LocalNotification,
} from '@amzn/aac-chrome-internal-api';
import AACChromeAPI from '@amzn/aac-chrome-api';
import type { RootState } from './store';
import {
  ActionInfo,
  AttributeTypes,
  PageTitleIconProps,
  POSITION,
} from '../features/header/components/PageHeader/types';
import {
  ChromeConfigurationPublisher,
  MobileNavPublisher,
} from '../commons/utils/metrics';

export { BANNER_TYPE } from '@amzn/aac-chrome-internal-api';

export enum VIEW_TYPE {
  DESKTOP,
  MOBILE,
}

export enum PAGE_LAYOUT_TYPE {
  STANDARD = 'STANDARD',
  LIGHT = 'LIGHT',
  FOCUS = 'FOCUS',
  FOCUS_NO_HEADER = 'FOCUS_NO_HEADER',
  INTERNAL_STANDARD = 'INTERNAL_STANDARD',
}

export enum BANNER_LEVEL {
  CONSOLE = 'console',
  PAGE = 'page',
}

export enum DEVICE_TYPE {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

// Define a type for the slice state
export interface Breadcrumb {
  url?: string;
  label: string;
  objectType?: string;
  objectSuffix?: string;
  onClickCallback?: () => void;
}

export interface BannerItem {
  id: string;
  title?: string;
  message: string;
  type: BANNER_TYPE;
  withCloseButton?: boolean;
}

export type FlagType = 'new' | 'beta' | 'beta_ai_with_icon';

export interface Flag {
  label: string;
  type?: string;
  description?: string;
  isInverted?: boolean;
  isMobile?: boolean;
}

export interface SectionNavItem {
  id: string;
  label: string;
  href?: string;
  flag?: Flag;
}

export interface Subsections {
  selectedItemId: string;
  items: SectionNavItem[];
}

export interface MarketplaceSwitcher {
  useMarketplaceSwitcher: boolean;
  marketplacePath?: string;
}

export interface CurrencyVisualisation {
  useCurrencyVisualisation: boolean;
}

export interface ObjectInfo {
  objectType?: string;
  objectName?: string;
  attributes?: Array<AttributeTypes>;
  actions?: { list: ActionInfo[] };
  attributeErrorMessage?: string;
  actionErrorMessage?: string;
}

export interface PageHeader {
  pageName: string;
  subsections?: Subsections;
  pageLevelBannerItems?: LocalNotification[];
  objectInfo?: ObjectInfo;
  pageScope?: 'ALL_ACCOUNTS' | 'SINGLE_ACCOUNT';
  pageTitleIcon?: PageTitleIconProps;
}

export interface PivotNavGroup {
  links: Array<PivotNavItem>;
  label?: string;
  selectedItemId: string;
  isLastGroup: boolean;
}

export interface PivotNavItem {
  label: string;
  id: string;
  toolTip?: string;
  tooltipPosition?: POSITION;
  url?: string;
  flag?: Flag;
}

export interface LaunchFlagProps {
  flag?: Flag;
}

export interface PivotNav {
  items?: Array<PivotNavItem>;
  selectedItemId: string;
  menuItemGroups?: Array<NestedPivotNavGroup>;
}

export interface NestedPivotNavGroup {
  links: Array<PivotNavItem>;
  label?: string;
  flag?: Flag;
}

export interface NestedPivotNav {
  menuItemGroups: Array<NestedPivotNavGroup>;
  selectedItemId: string;
  isCollapsable?: boolean;
}

export interface ChromeConfig {
  breadcrumbs: Breadcrumb[] | undefined;
  pageTitleBreadcrumb?: Breadcrumb;
  toastBanner?: BannerItem | undefined;
  mobileWebSupport: string;
  pageHeader?: PageHeader;
  pageId: string;
  pageLayoutType: PAGE_LAYOUT_TYPE;
  marketplaceSwitcher: MarketplaceSwitcher;
  regionSwitcherEnabled: boolean;
  currencyVisualisation: CurrencyVisualisation;
  nestedPivotNav: NestedPivotNav;
  onRouteClick?: (detail: { id: string; url: string }) => void;
  // Temporary support for old version of AACChromeAPI until all teams update to latest version of AACChromeAPI
  pivotNav?: PivotNav;
  appName: string;
  isGlobal: boolean;
  isAggregated: boolean;
}

export interface SharedState {
  chromeConfig: ChromeConfig;
  internalChromeContext: InternalChromeConfig;
  isMobileSideNavOpen: boolean;
  advertisingApiCsrfToken: string;
}

// Define the initial state using that type
export const sharedInitialState: SharedState = {
  chromeConfig: {
    ...JSON.parse(JSON.stringify(AACChromeAPI.getChromeConfiguration())),
  },
  internalChromeContext: {} as InternalChromeConfig,
  isMobileSideNavOpen: false,
  advertisingApiCsrfToken: '',
};

export const sharedSlice = createSlice({
  name: 'shared',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState: sharedInitialState,
  reducers: {
    updateChromeConfiguration: (state, action: PayloadAction<ChromeConfig>) => {
      ChromeConfigurationPublisher.publishCounterMonitor('Change', 1);
      return {
        ...state,
        chromeConfig: {
          ...state.chromeConfig,
          ...action.payload,
        },
      };
    },
    updateInternalChromeConfig: (
      state,
      action: PayloadAction<InternalChromeConfig>,
    ) => {
      return {
        ...state,
        internalChromeContext: {
          ...state.internalChromeContext,
          ...action.payload,
        },
      };
    },
    toggleMobileSideNav: (state) => {
      if (state.isMobileSideNavOpen) {
        MobileNavPublisher.publishCounterMonitor('Close', 1);
      } else {
        MobileNavPublisher.publishCounterMonitor('Open', 1);
      }
      state.isMobileSideNavOpen = !state.isMobileSideNavOpen;
    },
    updatePageHeader: (state, action: PayloadAction<PageHeader>) => {
      return {
        ...state,
        chromeConfig: {
          ...state.chromeConfig,
          pageHeader: {
            ...state.chromeConfig.pageHeader,
            ...action.payload,
          },
        },
      };
    },
    updateMarketplaceSwitcher: (
      state,
      action: PayloadAction<MarketplaceSwitcher>,
    ) => {
      return {
        ...state,
        chromeConfig: {
          ...state.chromeConfig,
          marketplaceSwitcher: {
            ...state.chromeConfig.marketplaceSwitcher,
            ...action.payload,
          },
        },
      };
    },
    setAdvertisingApiCsrfToken: (
      state,
      action: PayloadAction<{ csrfToken: string }>,
    ) => {
      state.advertisingApiCsrfToken = action.payload.csrfToken;
      return state;
    },
  },
});

export const {
  updateChromeConfiguration,
  updateInternalChromeConfig,
  toggleMobileSideNav,
  updatePageHeader,
  updateMarketplaceSwitcher,
} = sharedSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectIsMobileWeb = (state: RootState): boolean =>
  state.shared.chromeConfig?.mobileWebSupport === 'ENABLED' &&
  state.shared.internalChromeContext?.deviceType === DEVICE_TYPE.MOBILE;

export const selectMobileWebSupport = (state: RootState): string =>
  state.shared.chromeConfig?.mobileWebSupport || 'DISABLED';
/**
 * Selector to check if the context is in the seller app
 * which is flagged by the `isMobileWebView` prop on the ChromeContext
 */
export const selectIsMobileWebView = (state: RootState): boolean =>
  state.shared.internalChromeContext?.isMobileWebView || false;

export const selectIsForceDesktopSite = (state: RootState): boolean =>
  state.shared.internalChromeContext?.forceDesktopSite;

export const selectLastBreadcrumb = (state: RootState): Breadcrumb | null => {
  const breadcrumbs = state.shared.chromeConfig.breadcrumbs;
  return breadcrumbs && breadcrumbs.length > 0
    ? breadcrumbs[breadcrumbs.length - 1]
    : null;
};

export const selectChromeConfiguration = (state: RootState): ChromeConfig => {
  return state.shared.chromeConfig;
};

export const selectViewType = (state: RootState): VIEW_TYPE => {
  const isMobileWeb = selectIsMobileWeb(state);
  const isForceDesktop = selectIsForceDesktopSite(state);
  return isMobileWeb && !isForceDesktop ? VIEW_TYPE.MOBILE : VIEW_TYPE.DESKTOP;
};

export const selectDeviceType = (state: RootState): DEVICE_TYPE => {
  return state.shared.internalChromeContext?.deviceType || DEVICE_TYPE.DESKTOP;
};

export const selectEntityId = (state: RootState): string => {
  return state.shared.internalChromeContext?.entityId ?? '';
};

export const selectPageLayoutType = (state: RootState): PAGE_LAYOUT_TYPE => {
  return state.shared.chromeConfig.pageLayoutType;
};

export const selectIsMobileSideNavOpen = (state: RootState): boolean => {
  return state.shared.isMobileSideNavOpen;
};

export const selectPageLevelBanners = (
  state: RootState,
): LocalNotification[] => {
  return state.shared.chromeConfig.pageHeader?.pageLevelBannerItems || [];
};

export const selectObjectInfo = (state: RootState): ObjectInfo | undefined => {
  return state.shared.chromeConfig.pageHeader?.objectInfo;
};

export const selectPageTitleIconProps = (
  state: RootState,
): PageTitleIconProps | undefined => {
  return state.shared.chromeConfig.pageHeader?.pageTitleIcon;
};

export const selectAdvertisingApiCsrfToken = (state: RootState): string => {
  return state.shared.advertisingApiCsrfToken;
};

const { reducer, actions } = sharedSlice;

export const { setAdvertisingApiCsrfToken } = actions;

export default reducer;
